import React, { useEffect } from "react";
import { Modal, Spin, Empty, Checkbox, Button } from "antd";
import { FilePdfOutlined, DownloadOutlined } from "@ant-design/icons";

/**
 * Shared Report Preview Modal
 * Used by all report PDF previews. Renders a generated PDF (blob URL) inside an iframe
 * with an optional "Include filters" toggle and an optional "Download PDF" action.
 *
 * @param {boolean} visible - Modal visibility
 * @param {Function} onCancel - Close handler
 * @param {string} title - Modal title
 * @param {string} pdfDataUri - Blob URL / data URI of the generated PDF
 * @param {boolean} loading - Whether the PDF is still being generated
 * @param {boolean} includeFilters - Whether filters should be printed on the PDF
 * @param {Function} onIncludeFiltersChange - Filter toggle handler
 * @param {boolean} showFiltersOption - Show/hide the "Include filters" checkbox (default true)
 * @param {Function} onDownload - Optional download handler; renders a Download PDF button
 */
const ReportPreviewModal = ({
  visible,
  onCancel,
  title,
  pdfDataUri,
  loading,
  includeFilters,
  onIncludeFiltersChange,
  showFiltersOption = true,
  onDownload,
}) => {
  useEffect(() => {
    return () => {
      if (pdfDataUri && pdfDataUri.startsWith("blob:")) {
        URL.revokeObjectURL(pdfDataUri);
      }
    };
  }, [pdfDataUri]);

  return (
    <Modal
      title={
        <>
          <FilePdfOutlined style={{ color: "#ff4d4f", marginRight: 8 }} /> {title}
        </>
      }
      open={visible}
      onCancel={onCancel}
      width={850}
      style={{ top: 20 }}
      footer={null}
      destroyOnClose={true}
    >
      <div
        style={{
          marginBottom: 12,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <div>
          {showFiltersOption && (
            <Checkbox
              checked={includeFilters}
              onChange={(e) => onIncludeFiltersChange && onIncludeFiltersChange(e.target.checked)}
              style={{ fontSize: 14 }}
            >
              Include filter values at the bottom of the PDF
            </Checkbox>
          )}
        </div>
        {onDownload && (
          <Button type="primary" icon={<DownloadOutlined />} onClick={onDownload}>
            Download PDF
          </Button>
        )}
      </div>

      <div
        style={{
          minHeight: "65vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          background: "#f0f2f5",
          borderRadius: 8,
          padding: 16,
        }}
      >
        {loading && (
          <Spin size="large" />
        )}

        {!loading && pdfDataUri && (
          <iframe
            src={pdfDataUri}
            title={title}
            width="100%"
            height="650px"
            style={{ border: "1px solid #d9d9d9", borderRadius: 4, background: "#fff" }}
          />
        )}

        {!loading && !pdfDataUri && (
          <Empty description="No preview available" />
        )}
      </div>
    </Modal>
  );
};

export default ReportPreviewModal;
